import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      localStorage.setItem("pinpoint_token", data.token);
      toast.success("Password reset — you're signed in!");
      navigate("/");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-widest mb-4 justify-center"><ShieldCheck size={14} /> Reset your password</div>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-7" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center py-6">
              <Lock className="mx-auto mb-3 text-slate-400" size={30} />
              <h1 className="text-lg font-bold text-slate-900">Link not valid</h1>
              <p className="text-sm text-slate-500 mt-1">This reset link is missing its token. Please request a new one from the login page.</p>
              <button onClick={() => navigate("/login")} className="mt-5 text-sm font-medium text-blue-700 hover:text-blue-800">Go to login</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="flex items-center gap-2 mb-1"><KeyRound size={18} className="text-blue-600" /><h1 className="font-display text-xl font-bold text-slate-900">Choose a new password</h1></div>
              <p className="text-sm text-slate-500 mb-5">Enter a new password for your account below.</p>
              <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
              <input data-testid="reset-new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm password</label>
              <input data-testid="reset-confirm-password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-enter your password"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button type="submit" data-testid="reset-password-submit" disabled={busy}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Reset password & sign in
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">Powered by PinPoint</p>
      </div>
    </div>
  );
}
